import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType, getCMSImageURL } from "../../utils/cms.utils";
import ContentTag from "../../utils/tag";
import classNames from "classnames";
import {
  getAgeRangePublicRoute,
  isContentItemAllowedForAgeRange,
} from "../../utils/ageRange.util";
import ViewMoreCard from "../common/ViewMoreCard";
import { useInputMode } from "../../utils/hooks/useInputMode";
import OptimizedImage from "../common/OptimizedImage";
import NextPlayGuideHelper from "../../utils/nextPlayGuideHelper";

// ============================================================================
// CONSTANTS
// ============================================================================

var aspectGuide = {
  movie: "w-[250px] h-[350px]",
  searchbox: "w-[250px] h-[350px]",
  series: "w-[250px] h-[350px]",
  program: "h-[350px] w-[620px]",
  sport: "h-[350px] w-[620px]",
  music: "w-[250px] h-[250px]",
  animation: "w-[250px] h-[250px]",
  artist: "w-[215px] h-[215px]",
};

// ============================================================================
// COMPONENTS
// ============================================================================

var MovieItem = function (props) {
  var item = props.item || {};
  var onFocus = props.onFocus;
  var shapeType = props.shapeType;
  var nextPlayGuide = props.nextPlayGuide;

  var navigate = useNavigate();
  var contentPhotos = item.contentPhotos;
  var title = item.title;
  var year = item.year;
  var slug = item.slug;
  var tags = item.tags;
  var image = item.image;
  var farsiName = item.farsiName;
  var kind = item.kind;
  var contentType = item.contentType;

  var handleNavigate = function () {
    if (shapeType === "artist") {
      navigate("/artists/" + slug);
    } else {
      var contentKind = contentType?.kind || kind || "movie";
      if (contentKind === "music") {
        navigate("/" + contentKind + "/" + slug, {
          state: NextPlayGuideHelper.withGuideNavigationState(null, nextPlayGuide),
        });
      } else {
        navigate("/" + contentKind + "/" + slug);
      }
    }
  };

  var focusableResult = useFocusable({
    onFocus: onFocus,
    onEnterPress: handleNavigate,
  });

  var focused = focusableResult.focused;
  var ref = focusableResult.ref;
  var focusSelf = focusableResult.focusSelf;

  var inputModeResult = useInputMode();
  var setMouseFocus = inputModeResult.setMouseFocus;
  var handleMouseEnter = useCallback(function () {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  var getImage = function () {
    if (shapeType === "artist") {
      return { src: getCMSImageURL(image) };
    } else {
      return getOptimizedImageForType("verticalCover", contentPhotos, "TV", { width: 350, quality: 90, format: 'jpeg' });
    }
  };

  var isSeries = contentType?.kind === "series" || kind === "series";
  var imageData = getImage();

  return (
    <div className="legacy-showcase-item">
      {isSeries && (
        <div className="legacy-showcase-series-decoration">
          <img className="legacy-showcase-series-image" src="/serial_pseudo.svg" alt="" />
        </div>
      )}
      <div
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onClick={handleNavigate}
        className={classNames(
          "legacy-showcase-card",
          focused && "is-focused",
          "cursor-pointer",
          aspectGuide[shapeType]
        )}
      >
        {typeof imageData === 'string' ? (
          <img className="legacy-showcase-card-image" src={imageData} alt={title} />
        ) : (
          <OptimizedImage
            src={imageData.src}
            alt={title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
        {tags?.[0] && <ContentTag label={tags[0]} />}
        <div className="legacy-showcase-focus-overlay">
          <span className="legacy-showcase-card-title">{title}</span>
          <span className="legacy-showcase-card-year">{year}</span>
        </div>
      </div>
      {farsiName && <span className="legacy-showcase-card-artist-name">{farsiName}</span>}
    </div>
  );
};

export var MoviesSlider = function (props) {
  var movies = props.movies;
  var onFocus = props.onFocus;
  var rowTitle = props.rowTitle;
  var shapeType = props.shapeType || "movie";
  var id = props.id;
  var index = props.index;
  var actionLink = props.actionLink || null;
  var nextPlayGuide = props.nextPlayGuide;

  var ageRange = getAgeRangePublicRoute();
  var _items = movies.filter(function (x) {
    return isContentItemAllowedForAgeRange(x, ageRange);
  });

  var focusableResult = useFocusable({
    onFocus: onFocus,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left"],
    focusKey: "movie_slider_" + id,
    focusable: movies.length > 0,
  });

  var ref = focusableResult.ref;
  var focusKey = focusableResult.focusKey;

  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];

  var navigate = useNavigate();
  var scrollingRef = useRef(null);
  var innerRef = useRef(null);

  // Show View More card when items > 12
  var showViewMore = _items.length > 12;

  var inputModeResult = useInputMode();
  var isMouseFocus = inputModeResult.isMouseFocus;
  var onAssetFocus = useCallback(
    function (focusProps) {
      // Skip scroll adjustment on mouse focus
      if (isMouseFocus) return;
      var left = focusProps.left;
      if (left < 200) {
        var offset =
          shapeType === "sport" ? Math.abs(left - 500) : Math.abs(left - 300);
        innerRef.current.style.transform = "translateX(" + offset + "px)";
      } else {
        innerRef.current.style.transform = "translateX(0px)";
      }
    },
    [shapeType, isMouseFocus]
  );

  var paddingOffsetCount = shapeType === "sport" ? 2 : 5;

  if (_items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        ref={ref}
        className={classNames("legacy-showcase-row", shapeType === "music" && "legacy-showcase-row-music")}
        tabIndex={index}
      >
        <div className="legacy-showcase-row-title legacy-showcase-row-title-compact">{rowTitle}</div>
        <div
          className="legacy-showcase-row-scroller legacy-showcase-row-scroller-wide"
          ref={scrollingRef}
          style={
            selected > paddingOffsetCount
              ? { paddingRight: 0 }
              : { paddingRight: 40 }
          }
        >
          <div className="legacy-showcase-row-content legacy-showcase-row-content-relative" ref={innerRef}>
            {_items?.map(function (item, itemIndex) {
              return (
                <MovieItem
                  item={item}
                  key={"ms_" + item?.slug + "_" + id + "_" + itemIndex}
                  onFocus={function (e) {
                    setSelected(itemIndex);
                    onAssetFocus(e);
                  }}
                  shapeType={shapeType}
                  nextPlayGuide={nextPlayGuide}
                />
              );
            })}
            {/* View More Card - shown at end when items > 12 */}
            {showViewMore && (
              <div className="legacy-showcase-view-more">
                <ViewMoreCard
                  actionLink={actionLink || "category;" + id}
                  shapeType={shapeType}
                  focusKey={"view_more_" + id}
                  onFocus={function (e) {
                    setSelected(_items.length);
                    onAssetFocus(e);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
